/**
 * Cliente HTTP da aplicação.
 *
 * - Sempre envia cookies (credentials: 'include'): a sessão do usuário
 *   vive em cookie httpOnly, nunca em localStorage.
 * - Converte respostas de erro da API (formato ApiErrorBody) em ApiError,
 *   com mensagem pronta para exibir e o requestId para o suporte.
 */

export interface ApiErrorBody {
  statusCode: number;
  code: string;
  message: string;
  details?: unknown;
  requestId?: string;
  path: string;
  timestamp: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;
  readonly details?: unknown;

  constructor(status: number, body: Partial<ApiErrorBody>) {
    super(body.message ?? 'Não foi possível concluir a operação.');
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code ?? 'UNKNOWN';
    this.requestId = body.requestId;
    this.details = body.details;
  }
}

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  body?: unknown;
  signal?: AbortSignal;
  /** Status que não devem lançar erro (ex.: health check aceita 503). */
  acceptStatus?: number[];
}

async function request<T>(method: Method, path: string, options: RequestOptions = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers: options.body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, {
      code: 'NETWORK_ERROR',
      message: 'Sem conexão com o servidor. Verifique sua internet e tente novamente.',
    });
  }

  const isJson = response.headers.get('content-type')?.includes('application/json') ?? false;
  const data: unknown = isJson ? await response.json() : undefined;

  if (!response.ok && !options.acceptStatus?.includes(response.status)) {
    const error = new ApiError(response.status, (data ?? {}) as Partial<ApiErrorBody>);
    // Sessão expirada no meio do uso: avisa a aplicação para voltar ao login.
    if (error.status === 401 && error.code === 'NOT_AUTHENTICATED' && path !== '/auth/me') {
      window.dispatchEvent(new Event('auth:expired'));
    }
    throw error;
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, { ...options, body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, { ...options, body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>('DELETE', path, options),
};
